import './MobileBundleCard.scss';

import { Color } from '@ionic/core';
import { IonButton, IonCard, IonCardContent, IonCol, IonGrid, IonIcon, IonRow } from '@ionic/react';
import { chevronForward, warning } from 'ionicons/icons';
import React, { useCallback } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { assertNever } from 'src/utils/assert';
import { BundleMessages } from '../../../intl/BundleMessages';
import { DqText } from '../../dq-text';

export interface MobileBundleCardInfo {
  /** The page to route to on select. */
  routerLink?: string;
  /** The name of the bundle. */
  bundleName: string;
  /** The site name associated with the bundle. */
  siteName: string;
  /** The organization name associated with the bundle. */
  orgName?: string;
  /** The model number associated with the bundle. */
  model?: string;
  /** Information about the tubes in a bundle. */
  tubes?: {
    /** The number of tubes in the bundle. */
    count: number;
    /** The pattern type of the bundle's tubes. */
    pattern: 'square' | 'triangle';
  };
  /** When the bundle was created. */
  createdAt: number;
  /** The action button to display below the card. */
  action?: {
    /** The text of the action button. */
    text: string;
    /** The color variant of the the action button. */
    color: Color;
    /** Whether or not the button is disabled. */
    disabled?: boolean;
    /** The action that occurs on button press. */
    onPress: () => void;
  };
  /** Whether or not to display the "missing info" dialog. */
  isMissingInfo?: boolean;
}

interface MobileBundleCardProps extends MobileBundleCardInfo {}

export const MobileBundleCard: React.FC<MobileBundleCardProps> = props => {
  const onActionPress: React.MouseEventHandler<HTMLIonButtonElement> = useCallback(
    event => {
      event.preventDefault();
      event.stopPropagation();
      props.action?.onPress?.();
    },
    [props.action],
  );

  return (
    <IonCard className="bundle-card" routerLink={props.routerLink}>
      <IonCardContent>
        <IonGrid>
          <IonRow>
            <IonCol className="bundle-card-col">
              <DqText variant="h3">{props.bundleName}</DqText>
              <DqText variant="body-strong">{props.siteName}</DqText>
            </IonCol>
            {props.routerLink && (
              <IonCol size="auto" className="bundle-card-col ion-align-items-end">
                <IonButton color="primary" fill="clear" className="ion-no-padding" aria-hidden>
                  <IonIcon icon={chevronForward} slot="icon-only" />
                </IonButton>
              </IonCol>
            )}
          </IonRow>
          <IonRow>
            <IonCol className="bundle-card-col">
              {props.orgName && <DqText variant="body-small">{props.orgName}</DqText>}
              <DqText variant="body-small">
                <FormattedDate value={props.createdAt} day="numeric" year="numeric" month="short" />
              </DqText>
            </IonCol>
            {(props.tubes || props.model) && (
              <IonCol className="bundle-card-col ion-align-items-end">
                {props.tubes && (
                  <DqText variant="body-small" color="dark">
                    <FormattedMessage
                      {...BundleMessages.bundleCardTubeCount}
                      values={{ tubeCount: props.tubes.count }}
                    />
                    {' - '}
                    <FormattedMessage
                      {...(props.tubes.pattern === 'square'
                        ? BundleMessages.patternSquare
                        : props.tubes.pattern === 'triangle'
                          ? BundleMessages.patternTriangle
                          : assertNever(props.tubes.pattern))}
                    />
                  </DqText>
                )}
                {props.model && (
                  <DqText variant="body-small" color="dark">
                    {props.model}
                  </DqText>
                )}
              </IonCol>
            )}
          </IonRow>
          {props.isMissingInfo && (
            <IonRow>
              <IonCol>
                <IonCard className="bundle-warning ion-padding-horizontal">
                  <IonCardContent>
                    <div>
                      <IonIcon color="warning" icon={warning} />
                    </div>
                    <div>
                      <DqText variant="body-strong">
                        <FormattedMessage {...BundleMessages.missingInformation} />
                      </DqText>
                      <DqText>
                        {' - '}
                        <FormattedMessage {...BundleMessages.missingInformationDetails} />
                      </DqText>
                    </div>
                  </IonCardContent>
                </IonCard>
              </IonCol>
            </IonRow>
          )}
          {props.action && (
            <IonRow>
              <IonCol>
                <IonButton
                  expand="block"
                  fill="outline"
                  color="primary"
                  onClick={onActionPress}
                  disabled={props.action.disabled}
                >
                  {props.action.text}
                </IonButton>
              </IonCol>
            </IonRow>
          )}
        </IonGrid>
      </IonCardContent>
    </IonCard>
  );
};
